import React from "react";
import {
  Box,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
  Divider,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import {
  EmailOutlined,
  SmsOutlined,
  PhoneOutlined,
  LanguageOutlined,
  NotificationsActiveOutlined,
  GroupOutlined,
} from "@mui/icons-material";
import MultiCollaboratorSelect from "./MultiCollaboratorSelect";

interface NotificationSettings {
  enabled: boolean;
  sendEmail: boolean;
  minMessages: number;
  notificationUserIds: string[];
  channels: {
    website?: { enabled: boolean };
    sms?: { enabled: boolean };
    email?: { enabled: boolean };
    voice?: { enabled: boolean };
  };
  triggers: {
    response?: boolean;
    conversation_end?: boolean;
    daily_summary?: boolean;
  };
  emailSettings: {
    includeRecap?: boolean;
    includeFullConversation?: boolean;
    frequency?: string;
  };
}

interface AgentNotificationSettingsProps {
  settings: NotificationSettings;
  onChange: (settings: NotificationSettings) => void;
  disabled?: boolean;
  companyId: string;
  getToken: () => Promise<string>;
}

const AgentNotificationSettings: React.FC<AgentNotificationSettingsProps> = ({
  settings,
  onChange,
  disabled = false,
  companyId,
  getToken,
}) => {
  const handleToggle = (field: string, value: boolean) => {
    const newSettings = { ...settings };
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      (newSettings as any)[parent][child] = value;
    } else {
      (newSettings as any)[field] = value;
    }
    onChange(newSettings);
  };

  const handleChannelToggle = (channel: string, enabled: boolean) => {
    onChange({
      ...settings,
      channels: {
        ...settings.channels,
        [channel]: { enabled },
      },
    });
  };

  const handleTriggerToggle = (trigger: string, enabled: boolean) => {
    onChange({
      ...settings,
      triggers: {
        ...settings.triggers,
        [trigger]: enabled,
      },
    });
  };

  const handleEmailSettingToggle = (setting: string, value: any) => {
    onChange({
      ...settings,
      emailSettings: {
        ...settings.emailSettings,
        [setting]: value,
      },
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Main Toggle */}
      <Paper sx={{ p: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={settings.enabled}
              onChange={(e) => handleToggle("enabled", e.target.checked)}
              disabled={disabled}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <NotificationsActiveOutlined />
              <Typography variant="body1" fontWeight="medium">
                Enable Conversation Notifications
              </Typography>
            </Box>
          }
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 7, mt: 1 }}>
          Receive notifications when customers start conversations with this agent
        </Typography>
      </Paper>

      {/* Notification Recipients */}
      {settings.enabled && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <GroupOutlined />
            <Typography variant="h6">Notification Recipients</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which team members should receive notifications when
            conversations occur with this agent.
          </Typography>

          <MultiCollaboratorSelect
            companyId={companyId}
            getToken={getToken}
            value={settings.notificationUserIds || []}
            onChange={(userIds) =>
              onChange({
                ...settings,
                notificationUserIds: userIds,
              })
            }
            label="Notification Recipients"
            helperText="If no users are selected, notifications will be sent to the agent owner."
            disabled={disabled}
          />
        </Paper>
      )}

      {/* Channel Settings */}
      {settings.enabled && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Channel Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which channels trigger notifications
          </Typography>
          
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.channels.website?.enabled ?? true}
                  onChange={(e) => handleChannelToggle("website", e.target.checked)}
                  disabled={disabled}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LanguageOutlined fontSize="small" />
                  <Typography>Website Chat</Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.channels.email?.enabled ?? true}
                  onChange={(e) => handleChannelToggle("email", e.target.checked)}
                  disabled={disabled}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <EmailOutlined fontSize="small" />
                  <Typography>Email</Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.channels.sms?.enabled ?? true}
                  onChange={(e) => handleChannelToggle("sms", e.target.checked)}
                  disabled={disabled}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SmsOutlined fontSize="small" />
                  <Typography>SMS</Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.channels.voice?.enabled ?? true}
                  onChange={(e) => handleChannelToggle("voice", e.target.checked)}
                  disabled={disabled}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PhoneOutlined fontSize="small" />
                  <Typography>Voice Calls</Typography>
                </Box>
              }
            />
          </FormGroup>
        </Paper>
      )}

      {/* Email Settings */}
      {settings.enabled && settings.sendEmail && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Email Notification Settings
          </Typography>
          
          <FormGroup sx={{ gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.sendEmail}
                  onChange={(e) => handleToggle("sendEmail", e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Send Email Notifications"
            />
            
            {settings.sendEmail && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailSettings.includeRecap ?? true}
                      onChange={(e) =>
                        handleEmailSettingToggle("includeRecap", e.target.checked)
                      }
                      disabled={disabled}
                    />
                  }
                  label="Include Conversation Recap"
                />
                
                <FormControl size="small" sx={{ mt: 1 }}>
                  <InputLabel>Notification Frequency</InputLabel>
                  <Select
                    value={settings.emailSettings.frequency || "immediate"}
                    onChange={(e) =>
                      handleEmailSettingToggle("frequency", e.target.value)
                    }
                    label="Notification Frequency"
                    disabled={disabled}
                  >
                    <MenuItem value="immediate">Immediate</MenuItem>
                    <MenuItem value="hourly">Hourly Digest</MenuItem>
                    <MenuItem value="daily">Daily Summary</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </FormGroup>
        </Paper>
      )}

      {/* Advanced Settings */}
      {settings.enabled && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Advanced Settings
          </Typography>
          
          <TextField
            label="Minimum Messages Before Notification"
            type="number"
            size="small"
            value={settings.minMessages || 1}
            onChange={(e) =>
              onChange({
                ...settings,
                minMessages: Math.max(1, parseInt(e.target.value) || 1),
              })
            }
            disabled={disabled}
            inputProps={{ min: 1 }}
            helperText="Only notify after this many messages in a conversation"
            sx={{ mt: 2 }}
          />
        </Paper>
      )}

      {/* Info Chip */}
      {settings.enabled && (
        <Chip
          icon={<NotificationsActiveOutlined />}
          label="Notifications will be sent when new conversations start"
          color="info"
          variant="outlined"
          sx={{ alignSelf: "flex-start" }}
        />
      )}
    </Box>
  );
};

export default AgentNotificationSettings;